import { IScope, ILocaleService, IComponentOptions, IQService, IPromise } from "angular";
import { StateService } from "@uirouter/angularjs";
import dayjs from "@gtmhub/dayjs";
import { storage } from "@gtmhub/core/storage";
import timezones from "wwwroot/json/timezones.json";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IAccountNotification } from "@gtmhub/core/accounts";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { localize } from "@gtmhub/localization";
import { GtmhubController } from "@gtmhub/core";
import { AutomationService } from "@gtmhub/automation/services/automation";

export class CadenceCtrl extends GtmhubController {
  indicators: {
    loadingCadence?: boolean;
    saving?: boolean;
  };
  notifications: IAccountNotification;
  timezones: { value: string; name: string }[];
  weekdays: string[];
  cadences: string[];
  hourOptions: number[];
  minutesOptions: number[];
  showDatepickerCheckIn: boolean;
  showDatepickerReports: boolean;
  isRevampingReportsSettings: boolean;
  dayOfMonthReports: string;
  dayOfMonthCheckIn: string;
  todayDayOfMonth: string;
  isSlackIntegrated: boolean;
  private initialCadenceReports: string;
  private initialCadenceCheckIn: string;

  public static $inject = ["$scope", "AccountService", "$state", "$locale", "UIErrorHandlingService", "AnalyticsService", "AutomationService", "$q"];

  constructor(
    private $scope: IScope,
    private accountService: AccountService,
    private $state: StateService,
    private $locale: ILocaleService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private analyticsService: AnalyticsService,
    private automationService: AutomationService,
    private $q: IQService
  ) {
    super();
    this.init();
  }

  private init = (): IPromise<void> => {
    this.indicators = { loadingCadence: true };
    this.weekdays = this.$locale.DATETIME_FORMATS.DAY;

    this.$scope.$watch("notifications.checkinDeadline.periodStart", () => {
      if (this.notifications && this.notifications.cadenceCheckIn !== "Weekly") {
        this.notifications.checkinDeadline.dayOfTheWeek = dayjs(this.notifications.checkinDeadline.periodStart).format("dddd");
      }
    });

    this.$scope.$watch("notifications.reportsDeadline.periodStart", () => {
      if (this.notifications && this.notifications.cadenceReports !== "Weekly") {
        this.notifications.reportsDeadline.dayOfTheWeek = dayjs(this.notifications.reportsDeadline.periodStart).format("dddd");
      }
    });

    return this.$q.all([this.getAccount(), this.hasSlackIntegration()]).then(() => {
      this.indicators.loadingCadence = false;
      this.setOptions();
    });
  };

  featureAvailable = (name: string): boolean => {
    return this.accountService.featureAvailable(name);
  };

  formatWeekday = (cadence: string, weekday: string): string => {
    const localEvery = localize("everyWeekday");
    const localOn = localize("onWeekday");

    return cadence === "Weekly" ? `${localEvery} ${weekday}` : `${localOn} ${weekday}`;
  };

  formatDateBiWeekly = (date: string): string => {
    return dayjs(date).format("MMMM DD, YYYY");
  };

  formatDateMonthly = (date: string): string => {
    return dayjs(date).format("D");
  };

  toggleReminders = (): void => {
    this.notifications.checkinDeadline.disableConstraint = !this.notifications.checkinDeadline.disableConstraint;
  };

  toggleReports = (): void => {
    this.notifications.reportsDeadline.disableConstraint = !this.notifications.reportsDeadline.disableConstraint;
  };

  chooseCadenceCheckIn = (cadence: string): void => {
    this.notifications.cadenceCheckIn = cadence;
  };

  chooseCadenceReports = (cadence: string): void => {
    this.notifications.cadenceReports = cadence;
  };

  save = (): void => {
    this.indicators.saving = true;

    this.analyticsService.track("send okr update reminder", {
      notifications: this.notifications,
      turned_on: this.notifications.checkinDeadline.disableConstraint,
    });

    this.analyticsService.track("send aggregated okrs report", {
      notifications: this.notifications,
      turned_on: this.notifications.reportsDeadline.disableConstraint,
    });

    this.accountService.updateNotifications(this.notifications).then(
      () => {
        this.indicators.saving = false;
        this.$state.go("^.^");
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "saving", inline: true })
    );
  };

  closeCadenceModal = (): void => {
    this.$state.go("^.^");
  };

  setReportsCadenceDate = (num: number): void => {
    const date = dayjs().date(num).format("YYYY-MM-DDTHH:mm:ss[Z]");

    this.notifications.reportsDeadline.periodStart = date.toString();
    this.dayOfMonthReports = num.toString();
  };

  setCheckInCadenceDate = (num: number): void => {
    const date = dayjs().date(num).format("YYYY-MM-DDTHH:mm:ss[Z]");

    this.notifications.checkinDeadline.periodStart = date;
    this.dayOfMonthCheckIn = num.toString();
  };

  private getAccount = (): IPromise<void> => {
    return this.accountService.getAccount(storage.get("accountId")).then(
      (account) => {
        const today = dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]");

        this.notifications = account.notifications;
        this.dayOfMonthReports = dayjs(this.notifications.reportsDeadline.periodStart).utc().format("D");
        this.dayOfMonthCheckIn = dayjs(this.notifications.checkinDeadline.periodStart).utc().format("D");
        this.todayDayOfMonth = dayjs().utc().format("D");
        this.initialCadenceCheckIn = this.notifications.cadenceCheckIn;
        this.initialCadenceReports = this.notifications.cadenceReports;

        this.setWatchCadenceChange(today);
      },
      (error) => {
        console.error(error);
      }
    );
  };

  private hasSlackIntegration = (): IPromise<void> => {
    return this.automationService.hasSlackIntegration().then((hasIntegrationResponse) => {
      this.isSlackIntegrated = hasIntegrationResponse.hasIntegration;
    });
  };

  private setWatchCadenceChange = (today: string): void => {
    this.$scope.$watch("notifications.cadenceCheckIn", () => {
      if (
        this.notifications &&
        (this.notifications.cadenceCheckIn === "BiWeekly" || this.notifications.cadenceCheckIn === "Monthly") &&
        this.initialCadenceCheckIn !== "BiWeekly" &&
        this.initialCadenceCheckIn !== "Monthly"
      ) {
        this.notifications.checkinDeadline.periodStart = today;
        this.dayOfMonthCheckIn = dayjs().format("D");
      }
    });

    this.$scope.$watch("notifications.cadenceReports", () => {
      if (
        this.notifications &&
        (this.notifications.cadenceReports === "BiWeekly" || this.notifications.cadenceReports === "Monthly") &&
        this.initialCadenceReports !== "BiWeekly" &&
        this.initialCadenceReports !== "Monthly"
      ) {
        this.notifications.reportsDeadline.periodStart = today;
        this.dayOfMonthReports = dayjs().format("D");
      }
    });
  };

  private setOptions = (): void => {
    this.timezones = timezones;

    if (!this.weekdays) {
      this.weekdays = [];
    }

    if (!this.cadences) {
      this.cadences = [];
    }

    this.hourOptions = [];
    for (let i = 0; i < 24; i++) {
      this.hourOptions.push(i);
    }

    const minutesDifference = 5;
    this.minutesOptions = [];

    for (let i = 0; i < 60; i += minutesDifference) {
      this.minutesOptions.push(i);
    }
  };
}

export const CadenceComponent: IComponentOptions = {
  templateUrl: require("../views/cadence.jade"),
  controller: CadenceCtrl,
  bindings: {},
};
